import { useEffect, useRef, useState } from "react";
import { Card, Form, Offcanvas } from "react-bootstrap";
import { DashSquareFill, PlusSquareFill } from "react-bootstrap-icons";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill-new";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import CustomButton from "../../components/CustomButton/CustomButton";
import CommonQuill from "../../components/editor/CommonQuill";
import HelperService from "../../utility/HelperService";

interface AddWomenInEnergyProps {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

type FormData = {
  title: string;
  subTitle: string;
  slug: string;
  createdBy: string;
  meta_description: string;
  keyword: string;
};

const AddWomenInEnergy = ({
  show,
  handleClose,
  data,
}: AddWomenInEnergyProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    trigger
  } = useForm<FormData>();
  const [logo, setLogo] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [images, setImages] = useState<any[]>([{}]);
  const [shortDescription, setShortDescription] = useState("");
  const [fileError, setFileError] = useState<string>("");
  const descriptionQuillRef = useRef<any>(null);
  const shortDescQuillRef = useRef<any>(null);
  const [womens, setWomens] = useState<any[]>([{ title: "", image: null }]);
  const [events, setEvents] = useState<any[]>([
    {
      title: "",
      description: "",
      thumbUrl: "",
      videoUrl: "",
      link: "",
      publishDate: "",
    },
  ]);
  const [customErrors, setCustomErrors] = useState<{
    description?: string;
    short_description?: string;
  }>({});
  const [isLoading, setLoading] = useState(false);
  const imageHandler = (quillRef: any) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("storage", "ABOUT_US");

      try {
        const res: any = await WebService.fileUploadAPI({
          action: "upload",
          body: formData,
        });

        const imageUrl = res?.data?.url;
        if (!imageUrl) {
          toast.error("Image upload failed");
          return;
        }

        if (!quillRef?.current) return;

        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);

        editor.insertEmbed(range.index, "image", imageUrl);
        editor.setSelection(range.index + 1);
      } catch {
        toast.error("Image upload failed");
      }
    };
  };
  const isQuillEmpty = (value?: string) => {
    if (!value) return true;

    // remove html tags, &nbsp; and spaces
    const text = value
      .replace(/<(.|\n)*?>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();

    return text.length === 0;
  };
  const validateInfoTab = async () => {
    const titleValid = await trigger(["title"]);

    const newErrors: any = {};

    if (isQuillEmpty(description)) {
      newErrors.description = "Description is required";
    }

    if (isQuillEmpty(shortDescription)) {
      newErrors.short_description = "Short Description is required";
    }

    setCustomErrors(newErrors);

    // 🔥 IMPORTANT: return actual validity
    return titleValid && !newErrors.description;
  };

  const titleValue = watch("title");
  useEffect(() => {

    //Edit mode me slug ko kabhi change nahi karna
    if (data?._id) return;

    if (!titleValue) {
      setValue("slug", "");
      return;
    }

    // ✅ Only auto-generate in Add mode
    if (!data?._id) {
      const slug = HelperService.generateSlug(titleValue);
      setValue("slug", slug);
    }
  }, [titleValue, data]);
  useEffect(() => {
    if (data) {
      const storedUser = localStorage.getItem("UserData");
      const userData = storedUser ? JSON.parse(storedUser) : null;
      reset({
        title: data.title,
        subTitle: data.subtitle,
        slug: data.slug,
        keyword: data?.keyword,
        createdBy: userData?.fullName || "",
        meta_description: data?.meta_description,
      });
      setLogo(null);
      setShortDescription(data.short_description || "");
      setImages(data.images && data.images.length > 0 ? data.images : [{}]);
      setDescription(data.description);
      setWomens(
        data.womensEnergy?.length > 0
          ? data.womensEnergy
          : [{ title: "", image: null }]
      );
      setEvents(
        data.events?.length > 0
          ? data.events
          : [{ title: "", image: null, link: "" }]
      );
    }
  }, [data, show]);
  const ALLOWED_IMAGE_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/svg+xml",
  ];
  const uploadFile = (file: File, index: number, type: string) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("storage", "WOMEN_IN_ENERGY");
    WebService.fileUploadAPI({
      action: `upload`,
      body: formData,
    })
      .then((res: any) => {
        if (res.data) {
          if (type === "WOMEN") {
            womens[index].image = res.data?.url;
            setWomens([...womens]);
          } else if (type === "CRAUSAL") {
            images[index] = res.data?.url;
            setImages([...images]);
          } else if (type === "EVENT_THUMB") {
            events[index].thumbUrl = res.data?.url;
            setEvents([...events]);
          }
        }
        toast.success(res.message);
      })
      .catch((err: any) => {
        toast.error(err.response?.data?.message || "An error occurred");
      });
  };

  const onSubmit = async (request: FormData) => {
    const valid = await validateInfoTab();
    if (!valid) {
      toast.error("Please fill required fields");
      return;
    }
    setLoading(true);
    const formData = new FormData();
    const storedUser = localStorage.getItem("UserData");
    const userData = storedUser ? JSON.parse(storedUser) : null;
    if (logo) {
      formData.append("logo", logo);
    }
    if (data?._id) {
      formData.append("id", data._id);
    }
    if (!data?._id && userData?._id) {
      formData.append("createdBy", userData._id);
    }
    formData.append("title", request.title);

    const editor = descriptionQuillRef.current?.getEditor();
    const html = editor?.root.innerHTML || "";
    formData.append("description", html);
    formData.append("meta_description", request.meta_description);
    formData.append("short_description", shortDescription);
    formData.append("slug", request.slug || "");

    formData.append("keyword", request.keyword);
    if (
      images.length > 0 &&
      images.every((img) => img && Object.keys(img).length !== 0 && img !== "")
    ) {
      formData.append("images", JSON.stringify(images));
    }
    if (
      womens.length > 0 &&
      womens.every(
        (woman) => woman.title && woman.image && Object.keys(woman).length !== 0
      )
    ) {
      formData.append("womensEnergy", JSON.stringify(womens));
    }
    if (events.length > 0) {
      const cleanEvents = events.map((ev) => ({
        title: ev.title || "",
        description: ev.description || "",
        thumbUrl: ev.thumbUrl || "",
        videoUrl: ev.videoUrl || "",
        link: ev.link || "",
        publishDate: ev.publishDate || "",
      }));

      formData.append("events", JSON.stringify(cleanEvents));
    }
    WebService.fileUploadAPI({
      action: `women-in-energy/addContent`,
      body: formData,
    })
      .then((res: any) => {
        toast.success(res.message);
        setLoading(false);
        handleClose();
      })
      .catch((err: any) => {
        setLoading(false);
        toast.error(err.response?.data?.message || "An error occurred");
      });
  };

  return (
    <>
      <Offcanvas
        show={show}
        onHide={() => {
          handleClose();
        }}
        placement="end"
        className="offcanvas-custom"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{data ? "Edit" : "Add"}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form>
            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                Main Content
              </span>

              <Form.Group className="mb-2" controlId="title">
                <Form.Label className="label">Title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter title"
                  {...register("title", { required: "Title is required" })}
                />
                {errors.title && (
                  <div className="error-message">{errors.title.message}</div>
                )}
              </Form.Group>
              <Form.Group className="mb-2" controlId="slugField">
                <Form.Label className="label">Slug</Form.Label>{" "}
                <span className="text-danger">*</span>
                <Form.Control
                  type="text"
                  placeholder="Auto-generated slug"
                  {...register("slug")}
                  value={watch("slug") || ""}
                // disabled // ✅ keep it readonly
                />
              </Form.Group>
              <Form.Group className="mb-2" controlId="eventDescription">
                <Form.Label className="label">Description</Form.Label>

                <CommonQuill
                  ref={descriptionQuillRef}
                  value={description}
                  onChange={setDescription}
                  storage="WOMEN_IN_ENERGY"
                  initialHtml={show && data?.description ? HelperService.normalizeResizeHtml(data.description) : undefined}
                  onClearError={() =>
                    setCustomErrors((p) => ({
                      ...p,
                      description: undefined,
                    }))
                  }
                />
                {customErrors.description && (
                  <div className="error-message">
                    {customErrors.description}
                  </div>
                )}
              </Form.Group>
              <Form.Group className="mb-2" controlId="eventDescription">
                <Form.Label className="label">Short Description</Form.Label>

                <CommonQuill
                  ref={shortDescQuillRef}
                  value={shortDescription}
                  onChange={setShortDescription}
                  storage="WOMEN_IN_ENERGY"
                  initialHtml={show && data?.short_description ? HelperService.normalizeResizeHtml(data.short_description) : undefined}
                  onClearError={() =>
                    setCustomErrors((p) => ({
                      ...p,
                      short_description: undefined,
                    }))
                  }
                />
                {customErrors.short_description && (
                  <div className="error-message">
                    {customErrors.short_description}
                  </div>
                )}
              </Form.Group>
              <Card className="p-3 bg-light my-3 position-relative">
                <span
                  className="bg-white position-absolute rounded small"
                  style={{ top: "-12px", left: "12px", padding: "0 8px" }}
                >
                  Why Women in Energy?
                </span>
                <Form.Group className="mb-2" controlId="eventDescription">
                  {womens.map((activity, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("WomenIndex", index.toString());
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        const fromIndex = Number(
                          e.dataTransfer.getData("WomenIndex")
                        );
                        if (fromIndex === index) return;
                        const newWomens = [...womens];
                        const [moved] = newWomens.splice(fromIndex, 1);
                        newWomens.splice(index, 0, moved);
                        setWomens(newWomens);
                      }}
                      style={{ cursor: "move" }}
                    >
                      <div className="d-flex align-items-start">
                        <span
                          style={{
                            cursor: "grab",
                            marginRight: 8,
                            display: "flex",
                            alignItems: "center",
                            fontSize: 20,
                            color: "#888",
                            marginTop: 10,
                          }}
                          title="Drag to reorder"
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <circle cx="5" cy="6" r="1.5" />
                            <circle cx="5" cy="10" r="1.5" />
                            <circle cx="5" cy="14" r="1.5" />
                            <circle cx="11" cy="6" r="1.5" />
                            <circle cx="11" cy="10" r="1.5" />
                            <circle cx="11" cy="14" r="1.5" />
                          </svg>
                        </span>

                        <div style={{ flex: 1 }} className="highlight-item">
                          <Form.Control
                            type="file"
                            accept="image/*"
                            className="custom-image-upload"
                            onChange={(e) => {
                              const input = e.target as HTMLInputElement;
                              const file = input.files?.[0];

                              if (!file) return;

                              if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                                const msg =
                                  "Image format not supported. Please upload PNG, JPG, JPEG or SVG image.";

                                setFileError(msg);
                                toast.error(msg);
                                input.value = "";
                                return;
                              }

                              if (file) {
                                uploadFile(file, index, "WOMEN");
                              }
                              setFileError("");
                            }}
                          />
                          <p
                            className="text-primary mt-1"
                            style={{ fontSize: "13px" }}
                          >
                            Note: Please upload an image of size:{" "}
                            <strong>44 × 40 px</strong> in{" "}
                            <strong>.png, .jpeg, .jpg or .svg</strong> format.
                          </p>
                          {womens[index] && (
                            <img
                              src={
                                womens[index].image
                                  ? womens[index].image
                                  : data?.womens?.[index]?.imageUrl || ""
                              }
                              style={{
                                maxWidth: "100%",
                                maxHeight: 200,
                                objectFit: "contain",
                                marginTop: "5px",
                              }}
                            />
                          )}
                          <div className="d-flex mt-2">
                            <Form.Control
                              type="text"
                              placeholder={`Title`}
                              value={activity.title}
                              onChange={(e) => {
                                const newWomens = [...womens];
                                newWomens[index].title = e.target.value;
                                setWomens(newWomens);
                              }}
                            />
                            <div className="d-flex mt-2">
                              <PlusSquareFill
                                onClick={() => {
                                  setWomens([
                                    ...womens,
                                    { title: "", icon: null },
                                  ]);
                                }}
                                className="ms-2 me-2 cursor-pointer"
                              />
                              {womens.length > 1 && (
                                <DashSquareFill
                                  onClick={() => {
                                    const newWomens = womens.filter(
                                      (_, i) => i !== index
                                    );
                                    setWomens(newWomens);
                                  }}
                                  className="cursor-pointer"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </Form.Group>
              </Card>
              <Form.Group className="mb-2" controlId="createdBy">
                <Form.Label className="label">Created By</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Created by user"
                  {...register("createdBy")}
                  value={watch("createdBy") || ""}
                  disabled
                />
              </Form.Group>
            </Card>
            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                Media
              </span>
              <Form.Group className="mb-2" controlId="logo">
                <Form.Label className="label">Logo</Form.Label>
                <p
                  className="text-primary mt-1"
                  style={{ fontSize: "13px" }}
                >
                  Note: Please upload an image of size:{" "}
                  <strong>202 × 95 px</strong> in{" "}
                  <strong>.png, .jpeg, .jpg or .svg</strong> format.
                </p>
                <Form.Control
                  type="file"
                  accept="image/*"
                  className="custom-image-upload"
                  onChange={(e) => {
                    const input = e.target as HTMLInputElement;
                    const file = input.files && input.files[0];
                    if (file) {
                      setLogo(file);
                    }
                  }}
                />
              </Form.Group>
              {(logo || data?.logo) && (
                <div className="mb-2 event-image">
                  <img
                    src={logo ? URL.createObjectURL(logo) : data?.logo}
                    alt="Event"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 200,
                      objectFit: "contain",
                    }}
                  />
                </div>
              )}
              {/* <Form.Group className="mb-2" controlId="images">
                <Form.Label className="label">Images</Form.Label>
                {images.map((image, index) => (
                  <>
                    <div className="d-flex mt-2">
                      <Form.Control
                        type="file"
                        accept="image/*"
                        className="custom-image-upload"
                        onChange={(e) => {
                          const input = e.target as HTMLInputElement;
                          const file = input.files?.[0];

                          if (!file) return;

                          if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                            const msg =
                              "Image format not supported. Please upload PNG, JPG, JPEG or SVG image.";

                            setFileError(msg);
                            toast.error(msg);
                            input.value = "";
                            return;
                          }

                          if (file) {
                            uploadFile(file, index, "CRAUSAL");
                          }
                          setFileError("");
                        }}
                      />
                      <div className="d-flex mt-2">
                        <PlusSquareFill
                          onClick={() => {
                            setImages([...images, {}]);
                          }}
                          className="ms-2 me-2 cursor-pointer"
                        />
                        {images.length > 1 && (
                          <DashSquareFill
                            onClick={() => {
                              const newImages = images.filter(
                                (_, i) => i !== index
                              );
                              setImages(newImages);
                            }}
                            className="cursor-pointer"
                          />
                        )}
                      </div>
                    </div>
                    {image && Object.keys(image).length !== 0 && (
                      <div className="mt-2 event-image">
                        <img
                          src={image}
                          style={{
                            maxWidth: "100%",
                            maxHeight: 200,
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    )}
                  </>
                ))}
              </Form.Group> */}
            </Card>
            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                SEO
              </span>
              <Form.Label className="label">
                Meta Description
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                placeholder="Meta Description"
                {...register("meta_description", {
                  required: "Meta description is required",
                })}
              />
              {errors.meta_description && (
                <div className="error-message">
                  {errors.meta_description.message}
                </div>
              )}
              <Form.Group className="mb-2" controlId="eventName">
                <Form.Label className="label">
                  Keywords <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter keywords"
                  {...register("keyword", {
                    required: "Keywords are required",
                  })}
                />
                {errors.keyword && (
                  <div className="error-message">{errors.keyword.message}</div>
                )}
              </Form.Group>
            </Card>
          </Form>
        </Offcanvas.Body>
        <div className="d-flex justify-content-end p-3">
          <CustomButton
            isLoading={isLoading}
            label="Save"
            className="w-100"
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      </Offcanvas>
    </>
  );
};

export default AddWomenInEnergy;
